import React, {useCallback} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { User, Lock } from "lucide-react";
import {Link, useParams} from 'react-router-dom';
import {useIntl} from "react-intl";

import s from "./style.module.css";
import {changePasswordStore} from "../../store/change_password.store";
import {login} from "../../config/router/unauthorizedRouter";
import { Card, CardContent } from "../ui/card";
import { Input, PasswordInput } from "../ui/input";
import { Button } from "../ui/button";
import { Alert } from "../ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";

interface RouteParams {
  username: string;
  token: string;
}

const ChangePasswordPage: React.FC = () => {
  const { formatMessage } = useIntl();
  const { username, token } = useParams<RouteParams>();

  const formSchema = z.object({
    username: z.string().min(1, { message: formatMessage({ id: 'ENTER_USERNAME' }) }),
    password: z.string().min(1, { message: formatMessage({ id: 'ENTER_PASSWORD' }) }),
    repeat_password: z.string().min(1, { message: formatMessage({ id: 'REPEAT_PASSWORD' }) }),
  }).refine((data) => data.password === data.repeat_password, {
    message: "The new password that you entered do not match!",
    path: ["repeat_password"],
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: username || "",
      password: "",
      repeat_password: "",
    },
  });

  const changePassword = changePasswordStore(state => state.changePassword);
  const error = changePasswordStore(state => state.error);
  const message = changePasswordStore(state => state.message);
  const loading = changePasswordStore(state => state.loading);

  const onSubmit = useCallback((values: FormValues) => {
    changePassword(username, values.password, token);
  }, [changePassword, username, token]);

  return (
    <Card className={s.ChangePasswordPage}>
      <CardContent className="pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder={username}
                      startIcon={<User />}
                      disabled
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PasswordInput
                      placeholder={formatMessage({ id: 'PASSWORD' })}
                      startIcon={<Lock />}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="repeat_password"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PasswordInput
                      placeholder={formatMessage({ id: 'REPEAT_PASSWORD' })}
                      startIcon={<Lock />}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button variant="primary" type="submit" loading={loading} className="w-full">
              {formatMessage({ id: 'CHANGE_PASSWORD' })}
            </Button>

            {message && (
              <Alert variant="info">
                {message}
              </Alert>
            )}

            {error && (
              <Alert variant="error">
                {error.message}
              </Alert>
            )}

            <div className="flex justify-end">
              <Button variant="link" type="button" className="p-0">
                <Link to={login}>{formatMessage({ id: 'SIGN_IN' })}</Link>
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default ChangePasswordPage;
